import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Plane, Hotel, Accessibility, ArrowRight } from "lucide-react";
import { IMAGES } from "../data/images";
import CTABand from "../components/shared/CTABand";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function PlanPage() {
  return (
    <div data-testid="plan-page">
      {/* Full-height Niagara hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden pb-20" data-testid="niagara-hero">
        <img src={IMAGES.niagara} alt="Niagara Falls at night" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(9,13,22,0.3) 0%, rgba(9,13,22,0.9) 100%)" }} />
        <div className="relative z-10 container-xl pt-40">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">June 2027</div>
            <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-white mb-4"
              style={{ fontFamily: "var(--font-heading)" }}>Niagara Falls,<br />Ontario.</h1>
            <p className="text-lg max-w-xl" style={{ color: "var(--text-muted)" }}>
              One of Canada's most iconic natural wonders — the backdrop for Canada's most important AI event.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{ background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.3)" }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "var(--cyan)" }} />
              <span className="text-xs" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                Exact venue to be announced
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Niagara */}
      <section className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="why-niagara-section">
        <div className="container-xl">
          <RevealSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="overline-label mb-4">Why Niagara Falls</div>
                <h2 className="text-4xl font-black tracking-tight text-white mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  A destination worthy of the occasion.
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
                  Niagara Falls, Ontario sits at the heart of Canada's most densely connected technology corridor — within a 2-hour drive of Toronto, Hamilton, and the entire Golden Horseshoe. World-class hotel infrastructure, an international airport, and an iconic backdrop make it the perfect setting for Canada's premier AI event.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Plane, title: "Fly in from anywhere", desc: "Niagara Falls International Airport + YYZ (Toronto Pearson) — 90 min drive" },
                    { icon: MapPin, title: "Central location", desc: "2 hours from Toronto · 1.5 hours from Buffalo, NY · 4 hours from Ottawa" },
                    { icon: Hotel, title: "World-class accommodations", desc: "Major hotel brands with conference-adjacent properties along the Niagara Parkway" },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="flex items-start gap-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                          <Icon size={16} style={{ color: "var(--cyan)" }} />
                        </div>
                        <div>
                          <div className="font-semibold text-white text-sm mb-0.5">{item.title}</div>
                          <div className="text-sm" style={{ color: "var(--text-muted)" }}>{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="relative">
                <img src={IMAGES.niagara} alt="Niagara Falls" className="rounded-2xl w-full h-80 object-cover opacity-80" />
                <div className="absolute inset-0 rounded-2xl" style={{ background: "linear-gradient(to top, rgba(9,13,22,0.6) 0%, transparent 60%)" }} />
                <div className="absolute bottom-5 left-5">
                  <div className="text-xs uppercase tracking-widest" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>Niagara Falls, Ontario</div>
                  <div className="text-white font-bold text-sm" style={{ fontFamily: "var(--font-heading)" }}>June 2027</div>
                </div>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Travel & Hotel */}
      <section id="travel" className="section-py" data-testid="travel-section">
        <div className="container-xl">
          <RevealSection>
            <div className="overline-label mb-4">Travel & Hotel</div>
            <h2 className="text-4xl font-black tracking-tight text-white mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              Plan your stay.
            </h2>
            <p className="text-base mb-12" style={{ color: "var(--text-muted)" }}>
              Official hotel block and travel information will be released with the venue announcement. Join the priority list to be notified immediately.
            </p>
          </RevealSection>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Hotel, title: "Official Hotel Block", desc: "Negotiated group rates at conference-adjacent hotels. Priority list members get first access to room blocks.", status: "Coming with venue announcement" },
              { icon: Plane, title: "Flight Booking", desc: "Nearest airports: YQG (Windsor), YHM (Hamilton), and YYZ (Toronto Pearson). All within easy reach.", status: "YYZ recommended for international" },
              { icon: MapPin, title: "Ground Transportation", desc: "Airport shuttles, rental cars, and rideshare all available. Detailed transportation guide coming soon.", status: "Details with venue announcement" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <RevealSection key={item.title} delay={i * 0.1}>
                  <div className="card p-6 h-full" data-testid={`travel-card-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                      style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                      <Icon size={18} style={{ color: "var(--cyan)" }} />
                    </div>
                    <h4 className="font-bold text-white mb-2" style={{ fontFamily: "var(--font-heading)" }}>{item.title}</h4>
                    <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
                    <div className="text-xs px-3 py-1.5 rounded-full inline-block"
                      style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)", color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                      {item.status}
                    </div>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Venue TBA */}
      <section id="venue" className="section-py" style={{ backgroundColor: "var(--surface)" }} data-testid="venue-section">
        <div className="container-xl">
          <RevealSection>
            <div className="text-center max-w-2xl mx-auto">
              <MapPin size={40} className="mx-auto mb-6" style={{ color: "var(--cyan)", opacity: 0.7 }} />
              <div className="overline-label mb-4">Venue</div>
              <h2 className="text-4xl font-black tracking-tight text-white mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                To Be Announced.
              </h2>
              <p className="text-base mb-6" style={{ color: "var(--text-muted)" }}>
                We're finalizing a world-class venue in Niagara Falls, Ontario capable of hosting 1,500+ attendees, a full exhibition floor, multiple breakout tracks, and premium networking spaces. An announcement is coming soon.
              </p>
              <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full"
                style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)" }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "var(--cyan)" }} />
                <span className="text-sm" style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                  Join priority list for venue announcement notification
                </span>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Accessibility */}
      <section id="accessibility" className="section-py" data-testid="accessibility-section">
        <div className="container-xl">
          <RevealSection>
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.15)" }}>
                <Accessibility size={22} style={{ color: "var(--cyan)" }} />
              </div>
              <div className="max-w-2xl">
                <div className="overline-label mb-3">Accessibility</div>
                <h3 className="text-2xl font-black text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  An inclusive event for everyone.
                </h3>
                <p className="text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
                  Core AI Summit is committed to WCAG 2.2 AA accessibility standards across all event touchpoints — from the website to the venue. Specific accessibility accommodations will be detailed with the venue announcement. If you have immediate accessibility questions, please contact us directly.
                </p>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      <CTABand
        title="Ready to Come to Niagara?"
        subtitle="Join the priority list to be notified when the venue, dates, and hotel blocks are announced."
        primaryLabel="Join Priority List"
      />
    </div>
  );
}
